import { HAZARDS, TUNING } from '@cloudpearl/config';
import { maxReach } from './obstacles';
import type { Sim, SimInput } from './sim';
import type { Gate } from './gates';

const gates: Gate[] = [];

/**
 * A decent (not perfect) player for the open world: heads north, steers for wind gates and for
 * the next island, dives on downslopes, lets go before crests and island rims, dodges obstacles.
 * Used by tests, `?autopilot` demos and balancing.
 */
export function autopilot(sim: Sim, out: SimInput): SimInput {
  const w = sim.world;
  const hs = Math.sqrt(sim.vx * sim.vx + sim.vz * sim.vz);
  const ux = hs > 0.1 ? sim.vx / hs : 0;
  const uz = hs > 0.1 ? sim.vz / hs : -1;

  // Desired direction: north, pulled toward a gate ahead, or the next island.
  let dx = 0;
  let dz = -1;
  let bestGate = Infinity;
  for (const g of sim.gates.near(sim.x, sim.z - 60, 90, gates)) {
    const gx = g.x - sim.x;
    const gz = g.z - sim.z;
    if (gz > -8 || gz < -160 || Math.abs(gx) > -gz * 0.8) continue;
    if (-gz < bestGate) {
      bestGate = -gz;
      const l = Math.sqrt(gx * gx + gz * gz);
      dx = gx / l;
      dz = gz / l;
    }
  }
  if (w.islands && bestGate === Infinity) {
    // Aim for the next island along the chain we're on.
    const here = w.islandAt(sim.x, sim.z);
    if (here) {
      const next = w.nextInChain(here);
      const gx = next.cx - sim.x;
      const gz = next.cz - sim.z;
      const l = Math.sqrt(gx * gx + gz * gz);
      dx = gx / l;
      dz = gz / l;
    }
  }

  // Obstacle avoidance: anything whose body reaches our line in the next ~1.4 s (big mesas
  // can sit ~100 m off to the side and still be in the way).
  const look = Math.max(hs, 12) * 1.4;
  const big = maxReach(sim.map);
  const span = look + big;
  let avoid = 0;
  for (let cj = sim.field.cellOf(sim.z - span); cj <= sim.field.cellOf(sim.z + big); cj++) {
    for (let ci = sim.field.cellOf(sim.x - span); ci <= sim.field.cellOf(sim.x + span); ci++) {
      for (const o of sim.field.cell(ci, cj)) {
        const ox = o.x - sim.x;
        const oz = o.z - sim.z;
        const r = HAZARDS[o.kind].footprint * o.scale + 2.5;
        const along = ox * ux + oz * uz;
        if (along < -r || along > look + r) continue;
        const side = ox * -uz + oz * ux;
        if (Math.abs(side) < r) avoid += (side > 0 ? -1 : 1) * (1 - along / look + 0.3);
      }
    }
  }

  const err = -uz * dx + ux * dz;
  let steer = Math.max(-1, Math.min(1, err * 3)) + avoid * 1.4;
  steer = Math.max(-1, Math.min(1, steer));
  out.steer = Math.round(steer * TUNING.steerSteps);

  // Dive downhill, release before crests and rims.
  const ahead = 8;
  const h0 = w.height(sim.x, sim.z);
  const h1 = w.height(sim.x + ux * ahead, sim.z + uz * ahead);
  const edgeSoon = w.islands && (w.mask(sim.x + ux * 22, sim.z + uz * 22) < 0.5 || w.mask(sim.x + ux * 11, sim.z + uz * 11) < 0.5);
  if (sim.grounded) out.hold = (h1 - h0) / ahead < -0.02 && !edgeSoon;
  else {
    const below = w.mask(sim.x, sim.z) > 0.5;
    out.hold = below && sim.vy < -2 && sim.airTime > 0.3;
  }
  return out;
}
